import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Plus, Search, BedDouble, AlertTriangle, RefreshCw, Pencil, Trash2, Ban, RotateCcw, LogIn, LogOut, X, CalendarX,
} from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import Modal from "../components/Modal";
import ActionMenu, { type ActionMenuItem } from "../components/ActionMenu";
import BookingStatusBadge from "./BookingStatusBadge";
import RoomBookingForm from "./RoomBookingForm";
import { mastersApi, apiErrorMessage } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { formatCurrencyFull, formatDate, localToday } from "@/lib/data";
import { byNewest } from "@/lib/utils";
import type { BookingEvent, BookingPayload, BookingRoom, RoomBooking } from "./types";
import {
  ACTIVE_STATUSES, BOOKING_STATUSES, DEFAULT_TRANSITIONS, bookingStatusLabel, d10, dayDiff,
} from "./status";

type View = "all" | "today" | "upcoming";

const VIEWS: { value: View; label: string }[] = [
  { value: "all", label: "All" },
  { value: "today", label: "Staying today" },
  { value: "upcoming", label: "Upcoming" },
];

const fmtDay = (day: string) => (day ? formatDate(`${day}T00:00:00`) : "-");
const num = (v: unknown) => Number(v) || 0;

/** Toast after a status change, in plain words */
const STATUS_TOAST: Record<string, string> = {
  booked: "Booking is active again",
  checked_in: "Checked in",
  checked_out: "Checked out, the room is free",
  cancelled: "Booking cancelled, the room is free",
};

export default function RoomBookingsPage() {
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const today = localToday();

  const [bookings, setBookings] = useState<RoomBooking[]>([]);
  const [rooms, setRooms] = useState<BookingRoom[]>([]);
  const [events, setEvents] = useState<BookingEvent[]>([]);
  const [transitions, setTransitions] = useState<Record<string, string[]>>(DEFAULT_TRANSITIONS);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [view, setView] = useState<View>("all");
  const [statusFilter, setStatusFilter] = useState("");
  const roomFilter = searchParams.get("new") === "1" ? 0 : Number(searchParams.get("room")) || 0;

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<RoomBooking | null>(null);
  const [preset, setPreset] = useState<{ room_id?: number } | undefined>();
  const [deleting, setDeleting] = useState<RoomBooking | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const [bookingsRes, roomsRes, eventsRes] = await Promise.all([
        mastersApi.roomBookings.list(),
        mastersApi.rooms.list(),
        mastersApi.roomBookings.eventOptions(),
      ]);
      setBookings(bookingsRes.data?.data || bookingsRes.data || []);
      if (bookingsRes.status_transitions) setTransitions(bookingsRes.status_transitions);
      setRooms(roomsRes.data || []);
      setEvents(eventsRes.data || []);
      setLoadError(null);
    } catch (error) {
      setLoadError(apiErrorMessage(error, "Couldn't load room bookings"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "Book this room" from the Rooms page arrives as ?new=1&room=ID
  useEffect(() => {
    if (loading || searchParams.get("new") !== "1") return;
    const room = Number(searchParams.get("room")) || undefined;
    setEditing(null);
    setPreset(room ? { room_id: room } : undefined);
    setFormOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete("new");
    next.delete("room");
    setSearchParams(next, { replace: true });
  }, [loading, searchParams, setSearchParams]);

  // ─── Derived ─────────────────────────────────────────────────────────────

  /** Holding the room today: checked in, or due in on/before today and not yet out */
  const stayingToday = (b: RoomBooking) =>
    b.status === "checked_in" || (b.status === "booked" && d10(b.check_in) <= today && d10(b.check_out) > today);
  const isUpcoming = (b: RoomBooking) => b.status === "booked" && d10(b.check_in) > today;
  const inView = (b: RoomBooking, v: View) => (v === "today" ? stayingToday(b) : v === "upcoming" ? isUpcoming(b) : true);

  const counts = useMemo(() => {
    const scoped = roomFilter ? bookings.filter((b) => b.room_id === roomFilter) : bookings;
    return {
      all: scoped.length,
      today: scoped.filter(stayingToday).length,
      upcoming: scoped.filter(isUpcoming).length,
    } as Record<View, number>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings, roomFilter, today]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = bookings.filter((b) => {
      if (roomFilter && b.room_id !== roomFilter) return false;
      if (!inView(b, view)) return false;
      if (statusFilter && b.status !== statusFilter) return false;
      if (q) {
        const hay = [b.guest_name, b.event?.name, b.event?.client?.name, b.room?.name].map((v) => (v || "").toLowerCase());
        if (!hay.some((v) => v.includes(q))) return false;
      }
      return true;
    });
    // Upcoming: soonest arrival first; otherwise newest booking first
    return view === "upcoming"
      ? [...list].sort((a, b) => d10(a.check_in).localeCompare(d10(b.check_in)))
      : [...list].sort(byNewest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings, search, view, statusFilter, roomFilter, today]);

  const roomName = rooms.find((r) => r.id === roomFilter)?.name;
  const anyFilter = !!(search || statusFilter || view !== "all" || roomFilter);
  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setView("all");
    if (roomFilter) clearRoomFilter();
  };
  const clearRoomFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("room");
    setSearchParams(next, { replace: true });
  };

  // ─── Actions ─────────────────────────────────────────────────────────────

  const openNew = () => { setEditing(null); setPreset(roomFilter ? { room_id: roomFilter } : undefined); setFormOpen(true); };
  const openEdit = (b: RoomBooking) => { setEditing(b); setPreset(undefined); setFormOpen(true); };
  const closeForm = () => { setFormOpen(false); setEditing(null); setPreset(undefined); };

  /** Throws on failure so the form stays open and shows the server's message */
  const saveBooking = async (data: BookingPayload) => {
    if (editing) {
      const res = await mastersApi.roomBookings.update(editing.id, data);
      setBookings((prev) => prev.map((b) => (b.id === editing.id ? { ...b, ...res.data } : b)));
      showToast("Booking saved", "success");
    } else {
      const res = await mastersApi.roomBookings.create(data);
      setBookings((prev) => [res.data, ...prev]);
      showToast(`Room booked for ${data.guest_name}`, "success");
    }
    closeForm();
  };

  const changeStatus = async (b: RoomBooking, status: string) => {
    try {
      const res = await mastersApi.roomBookings.setStatus(b.id, status);
      setBookings((prev) => prev.map((x) => (x.id === b.id ? { ...x, ...(res.data || {}), status } : x)));
      showToast(`${STATUS_TOAST[status] || bookingStatusLabel(status)}: ${b.guest_name}`, "success");
    } catch (error) {
      showToast(apiErrorMessage(error, "Couldn't change the status"), "error");
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await mastersApi.roomBookings.delete(deleting.id);
      setBookings((prev) => prev.filter((b) => b.id !== deleting.id));
      showToast(`Booking for ${deleting.guest_name} deleted`, "success");
      setDeleting(null);
    } catch (error) {
      showToast(apiErrorMessage(error, "Couldn't delete the booking"), "error");
    } finally {
      setBusy(false);
    }
  };

  const cancelInstead = async () => {
    if (!deleting) return;
    setBusy(true);
    await changeStatus(deleting, "cancelled");
    setBusy(false);
    setDeleting(null);
  };

  const allowed = (b: RoomBooking, s: string) => (transitions[b.status] || []).includes(s);

  /** The one obvious next step for a booking, shown as a button */
  const primaryAction = (b: RoomBooking): { label: string; status: string; icon: typeof LogIn } | null => {
    if (b.status === "checked_in" && allowed(b, "checked_out")) return { label: "Check out", status: "checked_out", icon: LogOut };
    if (b.status === "booked" && d10(b.check_in) <= today && allowed(b, "checked_in")) return { label: "Check in", status: "checked_in", icon: LogIn };
    return null;
  };

  const menuItems = (b: RoomBooking): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [{ label: "Edit", icon: Pencil, onClick: () => openEdit(b) }];
    const primary = primaryAction(b);
    if (b.status === "booked" && allowed(b, "checked_in") && !primary) items.push({ label: "Check in", icon: LogIn, onClick: () => changeStatus(b, "checked_in") });
    if (b.status === "cancelled" && allowed(b, "booked")) items.push({ label: "Book again", icon: RotateCcw, onClick: () => changeStatus(b, "booked") });
    if (allowed(b, "cancelled")) items.push({ label: "Cancel booking", icon: Ban, separated: true, onClick: () => changeStatus(b, "cancelled") });
    items.push({ label: "Delete…", icon: Trash2, danger: true, separated: !allowed(b, "cancelled"), onClick: () => setDeleting(b) });
    return items;
  };

  // ─── Rendering helpers ───────────────────────────────────────────────────

  const nights = (b: RoomBooking) => Math.max(1, dayDiff(d10(b.check_out), d10(b.check_in)));
  const amount = (b: RoomBooking) => num(b.rate) * nights(b);

  /** Short "when" hint next to the dates */
  const whenPill = (b: RoomBooking) => {
    const inDay = d10(b.check_in);
    const outDay = d10(b.check_out);
    let pill: { text: string; tone: string } | null = null;
    if (b.status === "checked_in") {
      if (outDay === today) pill = { text: "Leaves today", tone: "bg-amber-100 text-amber-800" };
      else if (outDay < today) pill = { text: "Past check-out date", tone: "bg-red-50 text-red-700" };
    } else if (b.status === "booked") {
      const offset = dayDiff(inDay, today);
      if (outDay <= today) pill = { text: "Dates passed", tone: "bg-stone-100 text-stone-500" };
      else if (offset === 0) pill = { text: "Arrives today", tone: "bg-amber-600 text-white" };
      else if (offset < 0) pill = { text: "Due in, not checked in", tone: "bg-amber-100 text-amber-800" };
      else if (offset === 1) pill = { text: "Tomorrow", tone: "bg-amber-100 text-amber-800" };
      else if (offset <= 7) pill = { text: `in ${offset} days`, tone: "bg-stone-100 text-stone-600" };
    }
    return pill ? <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${pill.tone}`}>{pill.text}</span> : null;
  };

  const statusCell = (b: RoomBooking) => (
    <BookingStatusBadge status={b.status} options={transitions[b.status] || []} onChange={(s) => changeStatus(b, s)} />
  );

  const primaryButton = (b: RoomBooking, big = false) => {
    const p = primaryAction(b);
    if (!p) return null;
    const Icon = p.icon;
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); changeStatus(b, p.status); }}
        className={`${big ? "h-10 px-3.5 text-sm" : "h-8 px-2.5 text-xs"} inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold whitespace-nowrap`}
      >
        <Icon className="w-4 h-4" /> {p.label}
      </button>
    );
  };

  const dateRange = (b: RoomBooking) => `${fmtDay(d10(b.check_in))} – ${fmtDay(d10(b.check_out))}`;
  const muted = (b: RoomBooking) => b.status === "cancelled" || b.status === "checked_out";

  // ─── Loading / error ─────────────────────────────────────────────────────

  if (loading) {
    return (
      <DashboardLayout>
        <div className="h-6 w-40 rounded bg-stone-100 animate-pulse mb-2" />
        <div className="h-4 w-64 max-w-full rounded bg-stone-100 animate-pulse mb-5" />
        <div className="h-10 rounded-xl bg-stone-100 animate-pulse mb-3" />
        <div className="h-10 w-80 max-w-full rounded-xl bg-stone-100 animate-pulse mb-3" />
        <div className="bg-white border border-stone-200 rounded-xl divide-y divide-stone-100" aria-label="Loading room bookings">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="p-4 flex gap-4 items-center">
              <div className="flex-1 space-y-2">
                <div className="h-4 w-1/3 rounded bg-stone-100 animate-pulse" />
                <div className="h-3 w-1/4 rounded bg-stone-100 animate-pulse" />
              </div>
              <div className="h-5 w-20 rounded-full bg-stone-100 animate-pulse" />
            </div>
          ))}
        </div>
      </DashboardLayout>
    );
  }

  if (loadError) {
    return (
      <DashboardLayout>
        <div className="bg-white border border-stone-200 rounded-xl p-10 text-center">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <p className="font-semibold text-stone-800">Couldn't load room bookings</p>
          <p className="text-sm text-stone-500 mt-1">{loadError}</p>
          <button type="button" onClick={() => load()} className="mt-4 h-10 inline-flex items-center gap-2 px-4 rounded-xl border border-stone-200 text-sm font-semibold text-stone-700 hover:bg-stone-50">
            <RefreshCw className="w-4 h-4" /> Try again
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const chip = (active: boolean) =>
    `shrink-0 min-h-10 md:min-h-9 px-3 rounded-full border text-xs font-semibold whitespace-nowrap transition-colors ${
      active ? "border-amber-500 bg-amber-50 text-amber-800" : "border-stone-200 bg-white text-stone-600 hover:bg-stone-50"
    }`;

  const emptyState = (
    <div className="text-center py-12 px-4">
      {bookings.length === 0 ? (
        <>
          <BedDouble className="w-12 h-12 mx-auto mb-3 text-stone-300" />
          <p className="text-stone-600 font-medium">No room bookings yet</p>
          <button type="button" onClick={openNew} className="mt-4 h-10 inline-flex items-center gap-2 px-4 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90">
            <Plus className="w-4 h-4" /> New booking
          </button>
        </>
      ) : (
        <>
          <CalendarX className="w-12 h-12 mx-auto mb-3 text-stone-300" />
          <p className="text-stone-600 font-medium">No bookings match these filters</p>
          <button type="button" onClick={clearFilters} className="mt-3 min-h-10 text-sm font-semibold text-amber-700 hover:underline">Clear filters</button>
        </>
      )}
    </div>
  );

  return (
    <DashboardLayout>
      <div className="mb-4">
        <h1 className="text-xl font-bold text-stone-800">Room Bookings</h1>
        <p className="text-sm text-stone-500">Guest rooms booked for events. Tap a status to check guests in or out.</p>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search guest, event, client, room…"
            aria-label="Search room bookings"
            className="w-full h-10 pl-9 pr-3 rounded-xl border border-stone-200 bg-white text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
          />
        </div>
        <button
          type="button"
          onClick={openNew}
          className="h-10 inline-flex items-center justify-center gap-2 px-4 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 whitespace-nowrap"
        >
          <Plus className="w-4 h-4" /> New booking
        </button>
      </div>

      {/* Views (with counts) + status chips */}
      <div className="flex flex-col gap-2 mb-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1 p-1 rounded-xl bg-stone-100 border border-stone-200 overflow-x-auto max-w-full" role="group" aria-label="View">
            {VIEWS.map((v) => (
              <button
                key={v.value}
                type="button"
                aria-pressed={view === v.value}
                onClick={() => setView(v.value)}
                className={`shrink-0 h-9 px-3.5 rounded-lg text-sm whitespace-nowrap transition-colors ${view === v.value ? "bg-white text-stone-900 font-semibold shadow-sm" : "text-stone-600 hover:text-stone-900"}`}
              >
                {v.label} <span className="text-xs text-stone-400">{counts[v.value]}</span>
              </button>
            ))}
          </div>
          {roomFilter > 0 && (
            <button type="button" onClick={clearRoomFilter} className="h-10 inline-flex items-center gap-1.5 px-3 rounded-xl border border-amber-400 bg-amber-50 text-sm font-semibold text-amber-800">
              Room: {roomName || `#${roomFilter}`} <X className="w-4 h-4" />
            </button>
          )}
          {anyFilter && (
            <button type="button" onClick={clearFilters} className="h-10 inline-flex items-center gap-1 px-3 rounded-xl text-sm text-stone-600 hover:bg-stone-100">
              <X className="w-4 h-4" /> Clear
            </button>
          )}
        </div>
        <div className="flex gap-2 overflow-x-auto -mx-4 px-4 lg:mx-0 lg:px-0 pb-1" role="group" aria-label="Status">
          <button type="button" aria-pressed={!statusFilter} onClick={() => setStatusFilter("")} className={chip(!statusFilter)}>Any status</button>
          {BOOKING_STATUSES.map((s) => (
            <button key={s} type="button" aria-pressed={statusFilter === s} onClick={() => setStatusFilter(statusFilter === s ? "" : s)} className={chip(statusFilter === s)}>
              {bookingStatusLabel(s)}{" "}
              <span className="text-stone-400 font-normal">
                {bookings.filter((b) => b.status === s && inView(b, view) && (!roomFilter || b.room_id === roomFilter)).length}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
        {/* Phone cards */}
        <div className="md:hidden">
          {filtered.length === 0 ? emptyState : (
            <div className="divide-y divide-stone-100">
              {filtered.map((b) => (
                <div key={b.id} className={`p-4 ${muted(b) ? "opacity-80" : ""}`}>
                  <div className="flex items-start justify-between gap-3">
                    <button type="button" className="min-w-0 text-left" onClick={() => openEdit(b)}>
                      <p className={`font-semibold text-stone-800 break-words ${b.status === "cancelled" ? "line-through text-stone-500" : ""}`}>{b.guest_name}</p>
                      <p className="text-xs text-stone-500 mt-0.5 truncate">
                        {[b.event?.name, b.event?.client?.name].filter(Boolean).join(" · ") || "No event"}
                      </p>
                    </button>
                    {statusCell(b)}
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <div className="col-span-2 min-w-0">
                      <p className="text-[11px] text-stone-400">Dates</p>
                      <p className="text-stone-700">
                        {dateRange(b)} <span className="text-xs text-stone-400">· {nights(b)} night{nights(b) === 1 ? "" : "s"}</span>
                      </p>
                      <div className="mt-0.5">{whenPill(b)}</div>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] text-stone-400">Room</p>
                      <p className="text-stone-700 truncate">{b.room?.name || "-"}<span className="text-xs text-stone-400"> · {b.guest_count} guest{b.guest_count === 1 ? "" : "s"}</span></p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] text-stone-400">Amount</p>
                      <p className="font-semibold text-stone-800 tabular-nums truncate">{formatCurrencyFull(amount(b))}</p>
                      <p className="text-[11px] text-stone-400 tabular-nums">{formatCurrencyFull(num(b.rate))}/night</p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-end gap-2">
                    {primaryButton(b, true)}
                    <ActionMenu items={menuItems(b)} label={`More actions for ${b.guest_name}`} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          {filtered.length === 0 ? emptyState : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-stone-200 bg-stone-50">
                  <th className="px-3 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-stone-500">Guest</th>
                  <th className="px-3 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-stone-500">Event</th>
                  <th className="px-3 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-stone-500">Room</th>
                  <th className="px-3 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-stone-500">Dates</th>
                  <th className="px-3 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-stone-500">Amount</th>
                  <th className="px-3 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-stone-500">Status</th>
                  <th className="px-3 py-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((b) => (
                  <tr
                    key={b.id}
                    onClick={() => openEdit(b)}
                    className={`border-b border-stone-100 cursor-pointer transition-colors hover:bg-amber-50/40 ${stayingToday(b) ? "shadow-[inset_3px_0_0_#d97706]" : ""}`}
                  >
                    <td className="px-3 py-3 min-w-[160px]">
                      <p className={`text-sm font-semibold ${muted(b) ? "text-stone-500" : "text-stone-800"} ${b.status === "cancelled" ? "line-through" : ""}`}>{b.guest_name}</p>
                      <p className="text-xs text-stone-500">{b.guest_count} guest{b.guest_count === 1 ? "" : "s"}</p>
                    </td>
                    <td className="px-3 py-3">
                      <p className="text-sm text-stone-700">{b.event?.name || "-"}</p>
                      {b.event?.client?.name && <p className="text-xs text-stone-500">{b.event.client.name}</p>}
                    </td>
                    <td className="px-3 py-3 text-sm text-stone-700 whitespace-nowrap">
                      {b.room?.name || "-"}
                      {b.room?.type && <p className="text-xs text-stone-400">{b.room.type}</p>}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <p className={`text-sm ${muted(b) ? "text-stone-500" : "text-stone-700"}`}>
                        {dateRange(b)} <span className="text-stone-400">· {nights(b)} night{nights(b) === 1 ? "" : "s"}</span>
                      </p>
                      <div className="mt-0.5">{whenPill(b)}</div>
                    </td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      <p className="text-sm font-semibold text-stone-800 tabular-nums">{formatCurrencyFull(amount(b))}</p>
                      <p className="text-[11px] text-stone-400 tabular-nums">{formatCurrencyFull(num(b.rate))}/night</p>
                    </td>
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>{statusCell(b)}</td>
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1.5">
                        {primaryButton(b)}
                        <ActionMenu items={menuItems(b)} label={`More actions for ${b.guest_name}`} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {filtered.length > 0 && (
        <p className="mt-2 text-xs text-stone-400">
          {filtered.length} of {bookings.length} bookings · Booked and checked-in bookings hold the room; cancelled and checked-out ones free it
        </p>
      )}

      {/* Add / edit */}
      <Modal isOpen={formOpen} onClose={closeForm} title={editing ? "Edit room booking" : "New room booking"} size="md">
        {formOpen && (
          <RoomBookingForm
            key={editing?.id ?? `new-${preset?.room_id ?? ""}`}
            initialData={editing}
            preset={preset}
            rooms={rooms}
            events={events}
            bookings={bookings}
            onSubmit={saveBooking}
            onCancel={closeForm}
          />
        )}
      </Modal>

      {/* Delete */}
      {deleting && (
        <Modal
          isOpen={!!deleting}
          onClose={() => !busy && setDeleting(null)}
          title="Delete this booking?"
          size="sm"
          footer={
            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
              <button type="button" onClick={() => setDeleting(null)} disabled={busy} className="h-11 px-4 rounded-xl border border-stone-300 text-sm font-semibold text-stone-600 hover:bg-stone-50">Keep booking</button>
              {allowed(deleting, "cancelled") && (
                <button type="button" onClick={cancelInstead} disabled={busy} className="h-11 px-4 rounded-xl border border-stone-300 text-sm font-semibold text-stone-700 hover:bg-stone-50">Cancel booking instead</button>
              )}
              <button type="button" onClick={confirmDelete} disabled={busy} className="h-11 px-4 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50">Delete permanently</button>
            </div>
          }
        >
          <div className="space-y-3 text-sm text-stone-600">
            <p>
              <span className="font-semibold text-stone-800">{deleting.guest_name}</span>
              {deleting.room?.name ? ` in ${deleting.room.name}` : ""}, {dateRange(deleting)}.
            </p>
            <p>This removes the booking completely, including from the event's bill.</p>
            {ACTIVE_STATUSES.includes(deleting.status) && (
              <p className="rounded-lg bg-amber-50 text-amber-800 px-3 py-2">If the guest just isn't coming, cancel the booking instead. That frees the room and keeps the record.</p>
            )}
          </div>
        </Modal>
      )}
    </DashboardLayout>
  );
}
